import { useState, useEffect } from 'react';
import api from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Quick-link strip that renders above the M45 nav pills (EBiS | Encon.co.uk |
 * Price Changes | Phocas etc.).
 *
 * - Default: desktop-only (hidden md:flex)
 * - When inline=true: renders without the hidden-on-mobile guard, used inside
 *   the mobile hamburger drawer.
 */
const QuickLinksRow = ({ inline = false }) => {
  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [linksRes, groupsRes] = await Promise.all([
          api.get(`${API}/quick-links`),
          api.get(`${API}/quick-link-groups`),
        ]);
        if (cancelled) return;
        setQuickLinks(Array.isArray(linksRes.data) ? linksRes.data : []);
        setLinkGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      } catch (_) { /* silent — links are nice-to-have, never block the page */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const getLinksForGroup = (groupId) =>
    quickLinks
      .filter(link => (link.group_id || 'default') === groupId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleClick = (link) => {
    if (link?.url) window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  if (!quickLinks.length || !linkGroups.length) return null;

  const nonEmptyGroups = linkGroups.filter(g => getLinksForGroup(g.id).length > 0);
  if (nonEmptyGroups.length === 0) return null;

  return (
    <div className={`${inline ? 'flex' : 'hidden md:flex'} justify-center mb-2`}>
      <div className="flex flex-wrap justify-center items-center gap-1 px-4">
        {nonEmptyGroups.map((group, groupIndex) => {
          const groupLinks = getLinksForGroup(group.id);
          return (
            <span key={group.id} className="flex items-center">
              {groupIndex > 0 && (
                <span className="mx-3 text-gray-400 font-bold">||</span>
              )}
              {groupLinks.map((link, linkIndex) => (
                <span key={link.id} className="flex items-center">
                  <button
                    onClick={() => handleClick(link)}
                    className="hover:underline transition-all text-sm font-medium px-1"
                    style={{ color: group.color || '#6d28d9' }}
                    data-testid={`quick-link-${link.id}`}
                  >
                    {link.name}
                  </button>
                  {linkIndex < groupLinks.length - 1 && (
                    <span className="text-gray-300 mx-1">|</span>
                  )}
                </span>
              ))}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default QuickLinksRow;
